// Single-Source-of-Truth für License-Tier-Checks im Frontend.
//
// Bevorzugt das Backend-Flag `user.is_premium` aus /api/v2/me (kanonisch).
// Fällt nur dann auf Tier-String-Logik zurück, wenn das Flag nicht gesetzt ist
// (Legacy-Endpoints, alte Cached-Responses, Defensive-Default).
//
// Das vermeidet die Inkonsistenz aus pre-Refactor, wo z.B. Dashboard.jsx
// `power_paket_onetime` NICHT als Pro erkannt hat, weil die Inline-Liste
// es vergessen hatte.

export const PREMIUM_TIERS = [
  "trial",                  // Legacy — wird vom Backend wie Pro Solo behandelt
  "pro_solo_monthly",
  "pro_solo_annual",
  "power_paket_onetime",
];

const LIFETIME_TIERS = ["power_paket_onetime"];
const GRACE_STATUSES = ["cancelled", "expired"];
const TERMINAL_STATUSES = ["refunded"];

/**
 * Liefert true, wenn der User aktuell Pro-Zugriff hat.
 *
 * Quellpriorität:
 *  1. Backend-Flag `user.is_premium` (aus /api/v2/me)
 *  2. Fallback: Tier + Status + license_expires_at-Logik (mirrors backend/licensing.py)
 *  3. Anonymous / null → false
 */
export function isPremium(user) {
  if (!user) return false;

  // Source-of-truth: Backend-Flag wenn vorhanden.
  if (typeof user.is_premium === "boolean") {
    return user.is_premium;
  }

  // Fallback (Legacy-Endpoints, defensive).
  const tier = user.license_tier || "free";
  if (!PREMIUM_TIERS.includes(tier)) return false;

  if (LIFETIME_TIERS.includes(tier)) return true;

  const status = user.license_status || "active";
  if (TERMINAL_STATUSES.includes(status)) return false;

  if (GRACE_STATUSES.includes(status)) {
    const expires = user.license_expires_at
      ? new Date(user.license_expires_at)
      : null;
    if (!expires || isNaN(expires.getTime())) return false;
    return Date.now() < expires.getTime();
  }

  return true;
}

/** Gegenstück zu isPremium — convenience for jsx conditionals. */
export function isFree(user) {
  return !isPremium(user);
}

/** Lifetime-Tier (power_paket_onetime) — Sonderfall ohne Expiry. */
export function isLifetime(user) {
  return LIFETIME_TIERS.includes(user?.license_tier);
}

// ============================================================================
// Sprint 12+C (03.07.2026) — Pionier-Teaser-Modus
// ============================================================================

export const TAG_PIONIER = "pionier_flex4success";
export const TAG_FREE_USER = "free_user_flex4success";

/**
 * Prüft, ob der User zur Pionier-Kohorte gehört.
 *
 * Quellpriorität:
 *  1. Backend-Flag `user.is_pionier` (aus /api/warteliste/status oder /api/me/tags)
 *  2. Fallback: Tag `pionier_flex4success` in `user.klick_tipp_tags` oder `user.tags`
 *  3. Anonymous / null → false
 *
 * Wird für Pionier-Banner, Vorab-Zugang zur Flexpfad-Bibliothek und
 * tag-basierte Feature-Sichtbarkeit verwendet.
 */
export function isPionier(user) {
  if (!user) return false;

  if (typeof user.is_pionier === "boolean") {
    return user.is_pionier;
  }

  const tags = user.klick_tipp_tags || user.tags || [];
  if (!Array.isArray(tags)) return false;
  return tags.includes(TAG_PIONIER);
}
